import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useField } from 'formik';
import { MinusIcon, PlusIcon } from '@phosphor-icons/react';
import { createId } from '@paralleldrive/cuid2';

import { InputWrapper } from '../../../components/InputWrapper';
import { Input } from '../../../components/input/Input';
import { Button } from '../../../components/button/Button';
import { decimateAndFormatNumber, parseNumberInput } from '../../../utils/number';
import { Country, QuotationType } from 'src/types/enums';
import { getQuotationRollingAverage, type QuotationRollingAverageFilter } from '../models';

export interface IQuotationPriceInputProps {
  name: string;
  isDisabled?: boolean;

  quotationType: QuotationType | null;
  departurePostalCode: string;
  departureCountry: string;
  arrivalPostalCode: string;
  arrivalCountry: string;
}

export const QuotationPriceInput: React.FC<IQuotationPriceInputProps> = (props) => {
  const { name, isDisabled, quotationType, departurePostalCode, departureCountry, arrivalPostalCode, arrivalCountry } =
    props;
  const id = useId();
  const [field, meta, helpers] = useField({ name });
  const [suggestion, setSuggestion] = useState<number>(0);

  const filter: QuotationRollingAverageFilter | null = useMemo(() => {
    if (!quotationType || !departurePostalCode || !departureCountry || !arrivalPostalCode || !arrivalCountry) {
      return null;
    }

    return {
      type: quotationType,
      departurePostalCode,
      departureCountry: departureCountry as Country,
      arrivalPostalCode,
      arrivalCountry: arrivalCountry as Country,
    };
  }, [quotationType, departurePostalCode, departureCountry, arrivalPostalCode, arrivalCountry]);

  const fetchKey = useRef('');
  useEffect(() => {
    if (filter && !field.value) {
      const key = createId();
      fetchKey.current = key;
      const fetchSuggestions = async () => {
        const average = await getQuotationRollingAverage(filter);

        if (fetchKey.current === key) {
          setSuggestion(average ?? 0);
        }
      };
      fetchSuggestions();
    }
  }, [filter]);

  const modifyValue = (multiplier: number) => {
    if (field.value) {
      try {
        const parsedValue = parseNumberInput(field.value, 2);
        if (parsedValue) {
          helpers.setValue(decimateAndFormatNumber(parsedValue * multiplier, 2));
        }
      } catch (err) {
        console.error(err);
      }
    }
  };

  return (
    <InputWrapper labelText="Prijs" invalidText={meta.touched ? meta.error : undefined}>
      <div className="flex gap-2">
        <Input
          value={field.value}
          onChange={(val) => {
            helpers.setValue(val);
          }}
          onBlur={(evt) => {
            helpers.setTouched(true);
            field.onBlur(evt);
          }}
          id={id}
          type="number"
          step="0.01"
          isInvalid={Boolean(meta.touched && meta.error)}
          isDisabled={isDisabled}
        />

        <div>
          <Button
            onTrigger={() => {
              modifyValue(0.99);
            }}
          >
            <MinusIcon className="button-icon" />
          </Button>
        </div>
        <div>
          <Button
            onTrigger={() => {
              modifyValue(1.01);
            }}
          >
            <PlusIcon className="button-icon" />
          </Button>
        </div>
      </div>

      {!!suggestion && !field.value && (
        <div className="flex flex-wrap gap-1 mt-2">
          <div>Suggestie:</div>
          <div
            className="whitespace-nowrap cursor-pointer text-fg-inverse hover:bg-brand-hover bg-fg-muted rounded px-1"
            onClick={() => {
              helpers.setValue(decimateAndFormatNumber(suggestion, 2));
            }}
          >{`€ ${decimateAndFormatNumber(suggestion, 2, {
            decimalSeperator: ',',
            removeTrailingZeros: true,
          })}`}</div>
        </div>
      )}
    </InputWrapper>
  );
};
